import { connect } from 'react-redux';
import { ApplicationState } from '../../store';
import * as CustomerStateController from '../../store/StateControllers/Customers/CustomersStateController';
import { ScrollableContent, TopActionsAndScrollableContentContainer, TopActionButtons } from '../core/TopActionsAndScrollableContentContainer';
import { CustomerRequest, Purchase } from '../../store/Models/Customers/Customer';
import { MyCustomersProps } from './MyCustomersCommon';
import { TableColumn, TableRow, ToxnotReactTable } from '../core/ToxnotReactTable';
import { ToxnotReactComponentPaths } from '../core/ToxnotReactComponentPaths';
import { RouteComponentProps, withRouter } from 'react-router';
import { SearchBar } from '../core/SearchBar';
import { Filter, FilterId } from '../../store/Models/Filter';
import { MyCustomersRequestsFilteringManager } from '../../store/StateControllers/Customers/CustomerFiltering';
import FiltersContainer from '../core/FiltersContainer';
import { ToxnotSignInRequiredComponent } from '../core/BaseApplicationComponents/ToxnotSignInRequiredComponent';

import './MyCustomers.css';

type MyCustomerRequestProps =
    MyCustomersProps &
    RouteComponentProps;

interface MyCustomerRequestsState {
    activeFilters: Filter[];
    filteredRequests: CustomerRequest[];
    allRequests: CustomerRequest[];
}

class MyCustomerRequests extends ToxnotSignInRequiredComponent<MyCustomerRequestProps, MyCustomerRequestsState>{

    private _filteringManager: MyCustomersRequestsFilteringManager;

    // ===============================================================================

    public constructor(props: MyCustomerRequestProps) {
        super(props);

        this._filteringManager = new MyCustomersRequestsFilteringManager();

        this.state = {
            activeFilters: [],
            filteredRequests: [],
            allRequests: []
        }
    }

    // ===============================================================================

    protected override getDisallowedRoles() {
        return [];
    }

    // ===============================================================================

    public componentDidMount() {
        const requests = CustomerStateController.getCurrentlySelectedCustomerRequestsFromState(this.props);

        this.setState({
            filteredRequests: requests,
            allRequests: requests
        });
    }

    // ===============================================================================

    public renderConcreteComponent() {
        return (
            <TopActionsAndScrollableContentContainer shouldMakeTnSection={false}>
                <TopActionButtons>
                    <div id="requests-search-container">
                        <div>
                            <SearchBar localizedPlaceholder={this.localizeString("Seach by Requestor Name, Email, or Factory")} onSearch={this.addSearchFilter} clearSearchAfterSubmit={true} />
                        </div>
                        <div>
                            <FiltersContainer filters={this.state.activeFilters} getFriendlyNameForFilter={this.getFriendlyNameForFilter} removeFilter_click={this.removeFilter} />
                        </div>
                    </div>
                </TopActionButtons>
                <ScrollableContent>
                    <ToxnotReactTable
                        localizer={this.props.appServices}
                        allRowModels={this.state.filteredRequests}
                        headerRow={this.getRequestsTableHeaderRow()}
                        getRowForTable={this.getRequestsTableRow}
                        rowClickEventHandler={this.requestsTableRow_click}
                    />
                </ScrollableContent>
            </TopActionsAndScrollableContentContainer>
        );
    }

    // ===============================================================================

    private getRequestsTableHeaderRow() {
        return [
            { value: this.localizeString("Requested Items") },
            { value: this.localizeString("Report Types") },
            { value: this.localizeString("Requested By") },
            { value: this.localizeString("Requested On") },
            { value: this.localizeString("Response Date") },
        ] as TableColumn<CustomerRequest>[]
    }

    // ===============================================================================

    private getRequestsTableRow = (request: CustomerRequest) => {
        const reportTypesAndRegulations = this.getUserContextForSignedInUser().reportTypesAndRegulations;

        return {
            cols: [
                this.getInventoriesToReportText(request),
                CustomerStateController.getReportTypesAsDisplayableString(request, this.props.appServices, reportTypesAndRegulations.dynRegsForUser),
                request.requestData.requestContactName + `<${request.requestData.requestContactEmail}>`,
                new Date(request.dateCreated).toLocaleDateString(),
                request.dateResponded === null ? this.localizeString("Needs Response") : new Date(request.dateResponded).toLocaleDateString()
            ]
        } as TableRow;
    }

    // ===============================================================================

    private getInventoriesToReportText(request: CustomerRequest) {
        if (!request.requestData.openRequest) {
            return request.requestData.inventoriesToReport.length > 0 ? CustomerStateController.getInventoriesToReportOnAsString(request.requestData.inventoriesToReport, this.props.appServices) : this.localizeString("None selected")
        }

        return this.localizeString("Request for all purchased parts");
    }

    // ===============================================================================

    private setFilter = (filter: Filter, add: boolean) => {
        const newFilters = this.state.activeFilters.filter(f => f.filterId !== filter.filterId);

        if (add) {
            newFilters.push(filter);
        }

        const filteredRequests = this._filteringManager.getFilteredArray(this.state.allRequests, newFilters);

        this.setState({
            filteredRequests: filteredRequests,
            activeFilters: newFilters
        });
    }

    // ===============================================================================

    private addSearchFilter = (searchValue: string) => {
        this.setFilter({ filterId: FilterId.MyCustomerRequestsSearch, value: searchValue, filterParameter: null }, true);
    }

    // ===============================================================================

    private removeFilter = (filter: Filter) => {
        this.setFilter(filter, false);
    }

    // ===============================================================================

    private getFriendlyNameForFilter = (filter: Filter) => {
        if (filter.filterId === FilterId.MyCustomerRequestsSearch) {
            return this.localizeString("Search") + ": " + filter.value;
        }

        return filter.value;
    }

    // ===============================================================================

    //
    // UI Events
    //

    private requestsTableRow_click = (e: React.MouseEvent, request: CustomerRequest) => {
        this.props.history.push(ToxnotReactComponentPaths.MyCustomerDetailBasePath + "/" + encodeURIComponent(this.props.currentlySelectedCustomerId!) + "/Request/" + encodeURIComponent(request.requestId));
    }
}

// ===============================================================================

export default connect(
    (state: ApplicationState) => ({ appServices: state.appServices, ...state.myCustomersState }),
    CustomerStateController.actionCreators
)(withRouter(MyCustomerRequests as any));
